import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, AlertOctagon, FileDown, Search, AlertTriangle, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { api } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const TYPES = [
  "Contamination risk",
  "Temperature issue",
  "Raw material issue",
  "Hygiene issue",
  "Customer complaint",
  "Other",
];
const SEVERITIES = ["Low", "Medium", "High"];
const STATUS = ["OK", "Hold", "Discarded"];
const SEV_COLOR = { Low: "#1F4D3A", Medium: "#F4A261", High: "#D62828" };
const STATUS_COLOR = { OK: "#1F4D3A", Hold: "#F4A261", Discarded: "#D62828" };

const blank = () => ({
  incident_date: todayISO(),
  incident_time: new Date().toTimeString().slice(0, 5),
  reported_by: "",
  linked_batch_id: "",
  incident_type: "Other",
  description: "",
  severity: "Low",
  immediate_action: "",
  final_resolution: "",
  product_status: "OK",
});

export default function Incidents() {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  // Filters
  const [fBatch, setFBatch] = useState("");
  const [fSeverity, setFSeverity] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  // Affected customers modal
  const [affected, setAffected] = useState(null);

  const load = async () => {
    const [i, b] = await Promise.all([api.listIncidents(), api.listProductionBatches()]);
    setItems(i);
    setBatches(b);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const bat = (i.linked_batch_id || i.batch_id || "").toLowerCase();
      const d = i.incident_date || "";
      if (fBatch && !bat.includes(fBatch.toLowerCase())) return false;
      if (fSeverity !== "all" && i.severity !== fSeverity) return false;
      if (fFrom && d < fFrom) return false;
      if (fTo && d > fTo) return false;
      return true;
    });
  }, [items, fBatch, fSeverity, fFrom, fTo]);

  const save = async () => {
    if (!form.description) return toast.error("Description required");
    try {
      const created = await api.createIncident(form);
      toast.success("Incident logged");
      if (created.severity === "High" && created.linked_batch_id) {
        toast.warning("⚠ HIGH severity — recall recommended for this batch", { duration: 6000 });
      }
      setOpen(false);
      setForm(blank());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this incident?")) return;
    try { await api.deleteIncident(id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const downloadSingle = async (id) => {
    try { await api.downloadIncidentPdf(id); toast.success("PDF downloaded"); }
    catch { toast.error("PDF failed"); }
  };

  const downloadRange = async () => {
    try {
      const params = {};
      if (fFrom) params.from_date = fFrom;
      if (fTo) params.to_date = fTo;
      if (fBatch) params.batch_id = fBatch;
      if (fSeverity !== "all") params.severity = fSeverity;
      await api.downloadIncidentRangePdf(params);
      toast.success("Range PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No matching records");
    }
  };

  const showAffected = async (i) => {
    try {
      const r = await api.incidentAffectedCustomers(i.id);
      setAffected({ ...r, incident: i });
    } catch {
      toast.error("Could not load affected customers");
    }
  };

  const triggerRecall = async () => {
    const i = affected?.incident;
    if (!i?.linked_batch_id) return;
    const batch = batches.find((b) => b.batch_id === i.linked_batch_id);
    if (!batch) return toast.error("Batch not found");
    if (!window.confirm(`Mark batch ${batch.batch_id} as RECALLED?\nThis will tag ${affected.orders.length} orders as recalled.`)) return;
    try {
      const r = await api.recallProductionBatch(batch.id, `Triggered from incident: ${(i.description || "").slice(0, 100)}`);
      toast.success(`Recall complete: ${r.affected_orders} orders, ${r.affected_customers} clients`);
      setAffected(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recall failed");
    }
  };

  return (
    <div data-testid="page-incidents">
      <PageHeader
        overline="Omavalvonta · Food Safety"
        title="Incidents"
        subtitle="Incident logging with severity, traceability and recall capability."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadRange} data-testid="download-incidents-range">
              <FileDown size={16} className="mr-2" /> Export Range PDF
            </Button>
            <Button onClick={() => { setForm(blank()); setOpen(true); }} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-incident">
              <Plus size={16} className="mr-2" /> Log Incident
            </Button>
          </div>
        }
      />

      <div className="card-soft p-4 mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input value={fBatch} onChange={(e) => setFBatch(e.target.value)} placeholder="Batch" className="pl-9 font-mono text-xs" data-testid="inc-filter-batch" />
        </div>
        <Select value={fSeverity} onValueChange={setFSeverity}>
          <SelectTrigger className="text-xs" data-testid="inc-filter-severity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {SEVERITIES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="text-xs" data-testid="inc-filter-from" />
        <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="text-xs" data-testid="inc-filter-to" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={AlertOctagon} title="No incidents" description="Nothing to report — that's a good sign 👍" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((i) => {
            const isHigh = i.severity === "High";
            return (
              <div key={i.id} className="card-soft p-5 flex flex-col gap-2" data-testid={`incident-card-${i.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">
                      {formatDateEU(i.incident_date)} · {i.incident_time || ""}
                    </div>
                    {(i.linked_batch_id || i.batch_id) && (
                      <div className="font-mono text-sm text-[#E85D04] font-bold truncate">{i.linked_batch_id || i.batch_id}</div>
                    )}
                    <div className="text-xs text-[#6C757D]">{i.incident_type}</div>
                  </div>
                  <div className="flex flex-col gap-1 items-end shrink-0">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: `${SEV_COLOR[i.severity] || "#6C757D"}1f`, color: SEV_COLOR[i.severity] || "#6C757D" }}>
                      {i.severity}
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                      style={{ backgroundColor: `${STATUS_COLOR[i.product_status] || "#6C757D"}1f`, color: STATUS_COLOR[i.product_status] || "#6C757D" }}>
                      {i.product_status || "OK"}
                    </span>
                  </div>
                </div>
                <div className="font-semibold text-[#222] text-sm">{i.description || i.issue}</div>
                {(i.immediate_action || i.action) && (
                  <div className="text-xs bg-[#F7F1E8] rounded-md p-2">
                    <span className="text-[10px] uppercase tracking-wider text-[#6C757D] font-bold">Action:</span> {i.immediate_action || i.action}
                  </div>
                )}
                {i.final_resolution && (
                  <div className="text-xs bg-[#1F4D3A]/5 rounded-md p-2 text-[#222]">
                    <span className="text-[10px] uppercase tracking-wider text-[#1F4D3A] font-bold">Resolution:</span> {i.final_resolution}
                  </div>
                )}
                {isHigh && (i.linked_batch_id || i.batch_id) && (
                  <div className="bg-[#D62828]/10 border border-[#D62828]/30 rounded-md p-2 text-xs text-[#D62828] font-semibold flex items-center gap-2">
                    <AlertTriangle size={14} /> HIGH severity — review batch for recall
                  </div>
                )}
                <div className="text-[10px] text-[#6C757D]">By {i.reported_by || i.created_by || "—"}</div>
                <div className="flex justify-end gap-1 border-t border-[#E5E5E5] pt-2 mt-1">
                  {(i.linked_batch_id || i.batch_id) && (
                    <Button variant="ghost" size="sm" onClick={() => showAffected(i)} className="text-[#1F4D3A]" data-testid={`affected-${i.id}`}>
                      <UsersIcon size={14} className="mr-1" /> Affected
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => downloadSingle(i.id)} className="text-[#1F4D3A]" data-testid={`pdf-inc-${i.id}`}>
                    <FileDown size={14} className="mr-1" /> PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(i.id)} className="text-[#E76F51] hover:bg-[#E76F51]/10">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Incident Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="incident-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Log Incident</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <Input type="date" value={form.incident_date} onChange={(e) => setForm({ ...form, incident_date: e.target.value })} data-testid="inc-date" />
            </Field>
            <Field label="Time">
              <Input type="time" value={form.incident_time} onChange={(e) => setForm({ ...form, incident_time: e.target.value })} data-testid="inc-time" />
            </Field>
            <Field label="Reported By">
              <Input value={form.reported_by} onChange={(e) => setForm({ ...form, reported_by: e.target.value })} placeholder="Name" data-testid="inc-by" />
            </Field>
            <Field label="Linked Batch *">
              <Select value={form.linked_batch_id || "none"} onValueChange={(v) => setForm({ ...form, linked_batch_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="inc-batch"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {batches.slice(0, 30).map((b) => (
                    <SelectItem key={b.id} value={b.batch_id}>{b.batch_id} · {b.product}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Incident Type">
              <Select value={form.incident_type} onValueChange={(v) => setForm({ ...form, incident_type: v })}>
                <SelectTrigger data-testid="inc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Product Status">
              <div className="flex gap-1">
                {STATUS.map((s) => (
                  <Button key={s} type="button" size="sm"
                    variant={form.product_status === s ? "default" : "outline"}
                    className={form.product_status === s ? "text-white flex-1" : "flex-1"}
                    style={form.product_status === s ? { backgroundColor: STATUS_COLOR[s] } : {}}
                    onClick={() => setForm({ ...form, product_status: s })}
                    data-testid={`inc-status-${s.toLowerCase()}`}
                  >{s}</Button>
                ))}
              </div>
            </Field>
            <Field label="Severity" className="col-span-2">
              <div className="flex gap-2">
                {SEVERITIES.map((s) => (
                  <Button key={s} type="button"
                    variant={form.severity === s ? "default" : "outline"}
                    className={form.severity === s ? "text-white flex-1" : "flex-1"}
                    style={form.severity === s ? { backgroundColor: SEV_COLOR[s] } : {}}
                    onClick={() => setForm({ ...form, severity: s })}
                    data-testid={`inc-sev-${s.toLowerCase()}`}
                  >{s}</Button>
                ))}
              </div>
            </Field>
            <Field label="Description *" className="col-span-2">
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened?" data-testid="inc-desc" />
            </Field>
            <Field label="Immediate Action Taken" className="col-span-2">
              <Textarea rows={2} value={form.immediate_action} onChange={(e) => setForm({ ...form, immediate_action: e.target.value })} placeholder="What was done right away?" data-testid="inc-immediate" />
            </Field>
            <Field label="Final Resolution" className="col-span-2">
              <Textarea rows={2} value={form.final_resolution} onChange={(e) => setForm({ ...form, final_resolution: e.target.value })} placeholder="How was it fully resolved?" data-testid="inc-resolution" />
            </Field>
            {form.severity === "High" && (
              <div className="col-span-2 bg-[#D62828]/10 border border-[#D62828]/30 rounded-md p-3 text-sm text-[#D62828]">
                <AlertTriangle className="inline mr-1" size={16} />
                <strong>HIGH severity</strong> — after saving, click {"\""}Affected{"\""} on the card to view customers and trigger a recall if needed.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-incident">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Affected customers modal */}
      <Dialog open={!!affected} onOpenChange={(o) => !o && setAffected(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="affected-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <UsersIcon size={20} className="text-[#E85D04]" /> Affected Customers
            </DialogTitle>
          </DialogHeader>
          {affected && (
            <div className="space-y-3">
              <div className="bg-[#F7F1E8] rounded-lg p-3 text-sm">
                <div className="font-mono font-bold text-[#E85D04]">{affected.batch_id || "—"}</div>
                <div className="text-[#222]">{affected.product || "—"}</div>
                <div className="font-display font-bold text-2xl text-[#1F4D3A] mt-1">
                  {affected.orders.length} orders · {affected.customers} clients
                </div>
              </div>
              {affected.orders.length === 0 ? (
                <p className="text-sm text-[#6C757D]">No orders linked to this batch.</p>
              ) : (
                <div className="max-h-72 overflow-auto border border-[#E5E5E5] rounded-lg divide-y divide-[#E5E5E5]">
                  {affected.orders.map((o) => (
                    <div key={o.order_id} className="p-3 flex justify-between items-center text-sm">
                      <div>
                        <div className="font-semibold">{o.customer_name || "—"}</div>
                        <div className="text-[10px] text-[#6C757D] font-mono">Order {o.order_id.slice(0, 8)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[#222] font-bold">€{Number(o.subtotal || 0).toFixed(2)}</span>
                        {o.recalled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#D62828] text-white font-bold">RECALLED</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setAffected(null)}>Close</Button>
                {affected.orders.some((o) => !o.recalled) && (
                  <Button onClick={triggerRecall} className="bg-[#D62828] hover:bg-[#a51e1e] text-white" data-testid="trigger-recall">
                    <AlertTriangle size={16} className="mr-2" /> Trigger Recall
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
